/**
 * Heavy Bag Quiz - Interactive Quiz for Finding the Perfect Heavy Bag
 * Asks 5 questions and recommends products based on user answers
 */

class HeavyBagQuiz {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.currentQuestion = 1;
    this.totalQuestions = 5;
    this.answers = {};
    
    // Cache DOM elements
    this.modal = document.getElementById(`quiz-modal-${sectionId}`);
    this.triggerBtn = document.getElementById(`quiz-trigger-${sectionId}`);
    this.progressBar = document.getElementById(`quiz-progress-${sectionId}`);
    this.progressText = document.getElementById(`quiz-progress-text-${sectionId}`);
    this.quizBody = document.getElementById(`quiz-body-${sectionId}`);
    this.quizFooter = document.getElementById(`quiz-footer-${sectionId}`);
    this.resultsContainer = document.getElementById(`quiz-results-${sectionId}`);
    this.productsContainer = document.getElementById(`quiz-products-${sectionId}`);
    this.prevBtn = document.getElementById(`quiz-prev-${sectionId}`);
    this.nextBtn = document.getElementById(`quiz-next-${sectionId}`);
    
    // Load products data
    this.#loadProductsData();
    
    // Initialize event listeners
    this.#initEventListeners();
    
    console.log('🥊 Heavy Bag Quiz initialized for section:', sectionId);
  }

  #loadProductsData() {
    const dataElement = document.getElementById(`quiz-products-data-${this.sectionId}`);
    if (dataElement) {
      try {
        this.productsData = JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Failed to parse products data:', e);
        this.productsData = { products: [] };
      }
    } else {
      this.productsData = { products: [] };
    }
  }

  #initEventListeners() {
    // Trigger button
    if (this.triggerBtn) {
      this.triggerBtn.addEventListener('click', () => this.#openModal());
    }

    // Close buttons
    if (this.modal) {
      this.modal.querySelectorAll('[data-action="close-quiz"]').forEach(btn => {
        btn.addEventListener('click', () => this.#closeModal());
      });

      // Option buttons
      this.modal.querySelectorAll('.quiz-option').forEach(option => {
        option.addEventListener('click', (e) => this.#handleOptionSelect(e));
      });

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.#goToPrevQuestion());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.#goToNextQuestion());
      }

      // Retake button
      const retakeBtn = this.modal.querySelector('[data-action="retake-quiz"]');
      if (retakeBtn) {
        retakeBtn.addEventListener('click', () => this.#retakeQuiz());
      }

      // Keyboard navigation
      this.modal.addEventListener('keydown', (e) => this.#handleKeydown(e));
    }
  }

  #openModal() {
    if (!this.modal) return;
    
    // Store scroll position before locking
    this.scrollPosition = window.pageYOffset;
    
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quiz-modal-open');
    document.body.style.top = `-${this.scrollPosition}px`;
    
    // Focus first option
    setTimeout(() => {
      const firstOption = this.modal.querySelector('.quiz-question.active .quiz-option');
      if (firstOption) firstOption.focus();
    }, 100);
  }

  #closeModal() {
    if (!this.modal) return;
    
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quiz-modal-open');
    document.body.style.top = '';
    
    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
    
    // Return focus to trigger
    if (this.triggerBtn) {
      this.triggerBtn.focus();
    }
  }

  #handleKeydown(e) {
    if (e.key === 'Escape') {
      this.#closeModal();
    }
  }

  #handleOptionSelect(e) {
    const option = e.currentTarget;
    const questionEl = option.closest('.quiz-question');
    const questionNum = parseInt(questionEl.dataset.question);
    
    // Remove previous selection
    questionEl.querySelectorAll('.quiz-option').forEach(opt => {
      opt.classList.remove('selected');
    });
    
    // Add selection
    option.classList.add('selected');
    
    // Store answer with all data attributes
    this.answers[questionNum] = {
      value: option.dataset.value,
      weight: option.dataset.weight || null,
      type: option.dataset.type || null,
      setup: option.dataset.setup || null,
      budget: option.dataset.budget || null
    };
    
    // Enable next button
    this.#updateNavButtons();
    
    // Auto-advance after short delay
    setTimeout(() => {
      if (this.currentQuestion < this.totalQuestions) {
        this.#goToNextQuestion();
      } else {
        this.#showResults();
      }
    }, 300);
  }

  #goToPrevQuestion() {
    if (this.currentQuestion > 1) {
      this.currentQuestion--;
      this.#showQuestion(this.currentQuestion);
    }
  }

  #goToNextQuestion() {
    if (this.currentQuestion < this.totalQuestions && this.answers[this.currentQuestion]) {
      this.currentQuestion++;
      this.#showQuestion(this.currentQuestion);
    } else if (this.currentQuestion === this.totalQuestions && this.answers[this.currentQuestion]) {
      this.#showResults();
    }
  }

  #showQuestion(num) {
    // Hide all questions
    this.quizBody.querySelectorAll('.quiz-question').forEach(q => {
      q.classList.remove('active');
    });
    
    // Show current question
    const currentQ = this.quizBody.querySelector(`[data-question="${num}"]`);
    if (currentQ) {
      currentQ.classList.add('active');
      
      // Focus first option or selected option
      const selected = currentQ.querySelector('.quiz-option.selected');
      const firstOption = currentQ.querySelector('.quiz-option');
      if (selected) {
        selected.focus();
      } else if (firstOption) {
        firstOption.focus();
      }
    }
    
    // Hide results
    this.resultsContainer.classList.remove('active');
    this.modal.classList.remove('show-results');
    
    // Update progress
    this.#updateProgress();
    this.#updateNavButtons();
  }

  #updateProgress() {
    const percent = (this.currentQuestion / this.totalQuestions) * 100;
    if (this.progressBar) {
      this.progressBar.style.setProperty('--progress', `${percent}%`);
    }
    if (this.progressText) {
      this.progressText.textContent = `Question ${this.currentQuestion} of ${this.totalQuestions}`;
    }
  }

  #updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentQuestion === 1;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = !this.answers[this.currentQuestion];
      
      // Change text on last question
      if (this.currentQuestion === this.totalQuestions) {
        this.nextBtn.innerHTML = `
          See Results
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        `;
      } else {
        this.nextBtn.innerHTML = `
          Next
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        `;
      }
    }
  }

  #showResults() {
    // Hide all questions
    this.quizBody.querySelectorAll('.quiz-question').forEach(q => {
      q.classList.remove('active');
    });
    
    // Show results
    this.resultsContainer.classList.add('active');
    this.modal.classList.add('show-results');
    
    // Update progress to 100%
    if (this.progressBar) {
      this.progressBar.style.setProperty('--progress', '100%');
    }
    if (this.progressText) {
      this.progressText.textContent = 'Complete!';
    }
    
    // Calculate and display recommendations
    this.#calculateRecommendations();
  }

  #calculateRecommendations() {
    // Show loading
    this.productsContainer.innerHTML = `
      <div class="quiz-results__loading">
        <div class="quiz-results__spinner"></div>
        <span>Finding your perfect bags...</span>
      </div>
    `;
    
    // Simulate processing time for better UX
    setTimeout(() => {
      const recommendations = this.#scoreProducts();
      this.#renderProducts(recommendations);
    }, 800);
  }

  #scoreProducts() {
    const products = this.productsData.products || [];
    
    // Helper to extract bag weight from title (e.g., "300lb", "150lb")
    const extractBagWeight = (title) => {
      const match = title.match(/(\d+)\s*lb/i);
      return match ? parseInt(match[1]) : 0;
    };
    
    // Helper to extract bag height from title (e.g., "4ft", "5ft", "6ft")
    const extractBagHeight = (title) => {
      const match = title.match(/(\d+)\s*ft/i);
      return match ? parseInt(match[1]) : 0;
    };
    
    // Score each product based on answers
    const scoredProducts = products.map(product => {
      let score = 0;
      let maxPossibleScore = 0;
      const tags = (product.tags || []).map(t => t.toLowerCase());
      const title = (product.title || '').toLowerCase();
      const bagWeight = extractBagWeight(title);
      const bagHeight = extractBagHeight(title);
      
      // Experience Level Scoring (Q1) - max 10 points
      const expAnswer = this.answers[1];
      if (expAnswer) {
        maxPossibleScore += 10;
        if (expAnswer.value === 'beginner') {
          // Beginners: lighter bags (70-100lb), shorter bags (4ft)
          if (bagWeight > 0 && bagWeight <= 80) score += 10;
          else if (bagWeight > 80 && bagWeight <= 100) score += 8;
          else if (bagWeight > 100 && bagWeight <= 135) score += 5;
          else if (bagWeight > 135) score += 2;
        } else if (expAnswer.value === 'intermediate') {
          // Intermediate: medium bags (100-150lb), 4-5ft
          if (bagWeight >= 100 && bagWeight <= 135) score += 10;
          else if (bagWeight >= 80 && bagWeight < 100) score += 7;
          else if (bagWeight > 135 && bagWeight <= 150) score += 8;
          else if (bagWeight > 150) score += 4;
          else score += 3;
        } else if (expAnswer.value === 'advanced') {
          // Advanced: heavier bags (150-300lb), 5-6ft
          if (bagWeight >= 200) score += 10;
          else if (bagWeight >= 150) score += 8;
          else if (bagWeight >= 135) score += 5;
          else score += 2;
        }
      }
      
      // Training Purpose Scoring (Q2) - max 10 points
      const purposeAnswer = this.answers[2];
      if (purposeAnswer) {
        maxPossibleScore += 10;
        if (purposeAnswer.value === 'fitness') {
          // Fitness: medium weight bags, 4ft for compact workouts
          if (bagWeight >= 70 && bagWeight <= 100) score += 10;
          else if (bagWeight > 100 && bagWeight <= 135) score += 7;
          else if (bagWeight > 135) score += 4;
        } else if (purposeAnswer.value === 'technique') {
          // Technique: medium bags that don't swing too much
          if (bagWeight >= 100 && bagWeight <= 135) score += 10;
          else if (bagWeight >= 80 && bagWeight < 100) score += 7;
          else if (bagWeight > 135 && bagWeight <= 150) score += 6;
          else score += 3;
        } else if (purposeAnswer.value === 'power') {
          // Power training: heavier bags needed
          if (bagWeight >= 150) score += 10;
          else if (bagWeight >= 135) score += 7;
          else if (bagWeight >= 100) score += 4;
          else score += 2;
        } else if (purposeAnswer.value === 'mma') {
          // MMA/Muay Thai: longer bags for kicks (5-6ft), heavier
          if (bagHeight >= 5 && bagWeight >= 100) score += 10;
          else if (bagHeight >= 5) score += 8;
          else if (bagHeight === 4 && bagWeight >= 100) score += 6;
          else score += 3;
        }
      }
      
      // Body Weight Scoring (Q3) - max 15 points (most important)
      const weightAnswer = this.answers[3];
      if (weightAnswer) {
        maxPossibleScore += 15;
        if (weightAnswer.value === 'under-150') {
          // Under 150lbs person: 70-80lb bag ideal
          if (bagWeight >= 70 && bagWeight <= 80) score += 15;
          else if (bagWeight > 80 && bagWeight <= 100) score += 12;
          else if (bagWeight > 100 && bagWeight <= 135) score += 7;
          else score += 3;
        } else if (weightAnswer.value === '150-180') {
          // 150-180lbs person: 80-100lb bag ideal
          if (bagWeight >= 80 && bagWeight <= 100) score += 15;
          else if (bagWeight > 100 && bagWeight <= 135) score += 12;
          else if (bagWeight >= 70 && bagWeight < 80) score += 9;
          else if (bagWeight > 135 && bagWeight <= 150) score += 8;
          else score += 4;
        } else if (weightAnswer.value === '180-220') {
          // 180-220lbs person: 100-150lb bag ideal
          if (bagWeight >= 100 && bagWeight <= 135) score += 15;
          else if (bagWeight > 135 && bagWeight <= 150) score += 13;
          else if (bagWeight >= 80 && bagWeight < 100) score += 9;
          else if (bagWeight > 150) score += 8;
          else score += 3;
        } else if (weightAnswer.value === 'over-220') {
          // Over 220lbs person: 135lb+ bag ideal
          if (bagWeight >= 150) score += 15;
          else if (bagWeight >= 135) score += 13;
          else if (bagWeight >= 100) score += 9;
          else score += 4;
        }
      }
      
      // Training Location Scoring (Q4) - max 8 points
      const setupAnswer = this.answers[4];
      if (setupAnswer) {
        maxPossibleScore += 8;
        if (setupAnswer.value === 'home-small') {
          // Limited space: prefer 4ft and lighter bags
          if (bagHeight === 4 && bagWeight <= 100) score += 8;
          else if (bagHeight === 4 && bagWeight <= 135) score += 6;
          else if (bagHeight === 4) score += 4;
          else if (bagHeight === 5 && bagWeight <= 100) score += 5;
          else score += 2;
        } else if (setupAnswer.value === 'home-large') {
          // Spacious home gym: 4-5ft bags work well
          if (bagHeight === 4 || bagHeight === 5) score += 8;
          else if (bagHeight === 6) score += 5;
          else score += 3;
        } else if (setupAnswer.value === 'commercial') {
          // Commercial gym: can handle any size, prefer larger
          if (bagHeight >= 5 && bagWeight >= 150) score += 8;
          else if (bagHeight >= 5) score += 7;
          else if (bagWeight >= 150) score += 6;
          else score += 4;
        }
      }
      
      // Budget Scoring (Q5) - max 7 points
      const budgetAnswer = this.answers[5];
      if (budgetAnswer && product.price) {
        maxPossibleScore += 7;
        const priceInDollars = product.price / 100;
        
        if (budgetAnswer.value === 'budget') {
          if (priceInDollars <= 150) score += 7;
          else if (priceInDollars <= 200) score += 5;
          else if (priceInDollars <= 300) score += 3;
          else score += 0;
        } else if (budgetAnswer.value === 'mid-range') {
          if (priceInDollars >= 150 && priceInDollars <= 400) score += 7;
          else if (priceInDollars < 150) score += 5;
          else if (priceInDollars > 400 && priceInDollars <= 500) score += 4;
          else score += 2;
        } else if (budgetAnswer.value === 'premium') {
          if (priceInDollars >= 350 && priceInDollars <= 600) score += 7;
          else if (priceInDollars >= 250) score += 5;
          else if (priceInDollars >= 150) score += 3;
          else score += 1;
        } else if (budgetAnswer.value === 'professional') {
          if (priceInDollars >= 500) score += 7;
          else if (priceInDollars >= 400) score += 6;
          else if (priceInDollars >= 300) score += 4;
          else score += 2;
        }
      }
      
      // Availability bonus
      if (product.available) score += 2;
      maxPossibleScore += 2;
      
      // Calculate match percentage based on actual score vs max possible
      const matchPercent = maxPossibleScore > 0 
        ? Math.round((score / maxPossibleScore) * 100)
        : 50;
      
      // Clamp between 55% and 98%
      const clampedMatch = Math.min(98, Math.max(55, matchPercent));
      
      return {
        ...product,
        score,
        maxPossibleScore,
        bagWeight,
        bagHeight,
        matchPercent: clampedMatch
      };
    });
    
    // Sort by score and return top 4
    return scoredProducts
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }

  #renderProducts(products) {
    if (!products || products.length === 0) {
      this.productsContainer.innerHTML = `
        <div class="quiz-results__empty">
          <p>No products found. Please try different answers or browse our full collection.</p>
        </div>
      `;
      return;
    }
    
    let html = '';
    
    for (const product of products) {
      const hasComparePrice = product.compare_at_price && product.compare_at_price > product.price;
      
      html += `
        <a href="${product.url}" class="quiz-product-card">
          <div class="quiz-product-card__image">
            ${product.featured_image 
              ? `<img src="${product.featured_image}" alt="${product.title}" width="400" height="400">`
              : `<div class="quiz-product-card__placeholder"></div>`
            }
            <span class="quiz-product-card__match">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              ${product.matchPercent}% Match
            </span>
          </div>
          <div class="quiz-product-card__content">
            <h4 class="quiz-product-card__title">${product.title}</h4>
            <div class="quiz-product-card__price">
              ${product.price_formatted}
              ${hasComparePrice ? `<span class="quiz-product-card__price-compare">${product.compare_at_price_formatted}</span>` : ''}
            </div>
          </div>
        </a>
      `;
    }
    
    this.productsContainer.innerHTML = html;
  }

  #retakeQuiz() {
    // Reset state
    this.currentQuestion = 1;
    this.answers = {};
    
    // Clear all selections
    this.modal.querySelectorAll('.quiz-option').forEach(opt => {
      opt.classList.remove('selected');
    });
    
    // Show first question
    this.#showQuestion(1);
  }
}

// Make class available globally
window.HeavyBagQuiz = HeavyBagQuiz;

